export const SFH_DEFAULT_ORDER_LIMIT = 100_000;
export const SFH_KILO_GOLD_BAR_ORDER_LIMIT = 175_000;
export const SFH_HIGH_VALUE_SHIPPING_NOTICE_THRESHOLD = 25_000;
export const SFH_COMBINED_LIVE_REQUESTS_THRESHOLD = 250_000;
export const SFH_ORDER_WEIGHT_LIMIT_LBS = 38;

// MetalType can be the string label ("Gold") or the numeric API enum (Gold=1).
const GOLD_METAL_LABELS = new Set(['gold', '1']);

// Shipping weight uses avoirdupois: 16 oz = 1 lb (38 lbs = 608 oz).
const OZ_TO_LBS = 1 / 16;
const KG_TO_LBS = 2.2046226218;
const G_TO_LBS = KG_TO_LBS / 1000;

const isGoldMetalType = (metalType) =>
  GOLD_METAL_LABELS.has(
    String(metalType ?? '')
      .trim()
      .toLowerCase(),
  );

const isOneKiloWeight = (weightCaption) => {
  const normalized = String(weightCaption ?? '')
    .trim()
    .toLowerCase();
  return /^1[\s-]?(?:kilo(?:gram)?s?|kg)\b/.test(normalized);
};

export const isOneKiloGoldBar = (selectedHit) =>
  isGoldMetalType(selectedHit?.MetalType) &&
  isOneKiloWeight(selectedHit?.WeightCaption);

export const resolveSfhOrderLimit = (selectedHit) =>
  isOneKiloGoldBar(selectedHit)
    ? SFH_KILO_GOLD_BAR_ORDER_LIMIT
    : SFH_DEFAULT_ORDER_LIMIT;

/**
 * Parse a product WeightCaption (e.g. "1oz", "1/10 oz", "1 kilo") into pounds.
 */
export const parseWeightCaptionToLbs = (weightCaption) => {
  const normalized = String(weightCaption ?? '')
    .trim()
    .toLowerCase();
  if (!normalized) return 0;

  const kiloMatch = normalized.match(
    /^(\d+(?:\.\d+)?)\s*(?:kilo(?:gram)?s?|kg)\b/,
  );
  if (kiloMatch) return Number(kiloMatch[1]) * KG_TO_LBS;

  const gramMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*(?:g|grams?)\b/);
  if (gramMatch) return Number(gramMatch[1]) * G_TO_LBS;

  const fracOzMatch = normalized.match(
    /^(\d+)\s*\/\s*(\d+)\s*(?:oz|ounces?)\b/,
  );
  if (fracOzMatch) {
    const denominator = Number(fracOzMatch[2]);
    if (!denominator) return 0;
    return (Number(fracOzMatch[1]) / denominator) * OZ_TO_LBS;
  }

  const ozMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*(?:oz|ounces?)\b/);
  if (ozMatch) return Number(ozMatch[1]) * OZ_TO_LBS;

  return 0;
};

export const resolveSfhOrderWeightLbs = (selectedHit, quantity) =>
  parseWeightCaptionToLbs(selectedHit?.WeightCaption) * (Number(quantity) || 0);

export const isSfhOrderOverWeightLimit = (selectedHit, quantity) =>
  resolveSfhOrderWeightLbs(selectedHit, quantity) > SFH_ORDER_WEIGHT_LIMIT_LBS;
